import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { parseConvertedChildrenFromApiResponse } from './document-conversion-lock.util';
import { DocumentService } from './document.service';
import {
  buildStockLignesFromPvDocumentItems,
  mapErpLignesToPvRegularisationItems,
  readMergedDocumentHidden,
} from './pv-ecart-regularisation-flow.util';
import { STOCK_TRANSFER_WORKFLOW_TYPE_IDS } from './stock-transfer-workflow.constants';
import {
  isBonTransfertDocumentFromPvEcart,
  resolveTransferWorkflowRootId,
} from './stock-transfer-workflow.util';

function pickLatestChildByType(children: any[], typeId: number): any | null {
  const list = (children || []).filter((c) => Number(c?.type_document_id) === typeId);
  if (!list.length) {
    return null;
  }
  return list.sort((a, b) => Number(b?.id) - Number(a?.id))[0];
}

/** Numéro LC du transfert entrepôt (référence des réservations disponible). */
export function readPvEcartLcTransfertNumFromDoc(
  doc: Record<string, unknown> | null | undefined
): string | null {
  const hidden = readMergedDocumentHidden(doc ?? null);
  const num = String(hidden['stock_transfert_num_document'] ?? '').trim();
  if (num) {
    return num;
  }
  return parseStockTransfertNumFromPvEcartNote(doc?.['note']);
}

/** Ex. note « PV d'écart — transfert LC 00000113 (#196) » */
export function parseStockTransfertNumFromPvEcartNote(note: unknown): string | null {
  const text = String(note ?? '').trim();
  if (!text) {
    return null;
  }
  const m = text.match(/transfert\s+LC\s+(\d+)/i);
  if (!m?.[1]) {
    return null;
  }
  const digits = m[1].replace(/\D/g, '');
  if (digits.length < 4) {
    return null;
  }
  return digits.padStart(8, '0');
}

export function resolvePvEcartLcReservationReference$(
  documentService: DocumentService,
  doc: Record<string, unknown>
): Observable<string | null> {
  const local = readPvEcartLcTransfertNumFromDoc(doc);
  if (local) {
    return of(local);
  }
  const rootId = resolveTransferWorkflowRootId(doc);
  if (rootId == null || rootId <= 0) {
    return of(null);
  }
  return documentService.getDocumentsByOriginalId(rootId).pipe(
    map((res) => {
      const children = parseConvertedChildrenFromApiResponse(res);
      const pv = pickLatestChildByType(children, STOCK_TRANSFER_WORKFLOW_TYPE_IDS.PV_ECART);
      if (pv && typeof pv === 'object') {
        const fromPv = readPvEcartLcTransfertNumFromDoc(pv as Record<string, unknown>);
        if (fromPv) {
          return fromPv;
        }
      }
      const bt = pickLatestChildByType(children, STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_TRANSFERT);
      if (bt && typeof bt === 'object') {
        return readPvEcartLcTransfertNumFromDoc(bt as Record<string, unknown>);
      }
      return null;
    }),
    catchError(() => of(null))
  );
}

/** Bon de sortie / réception de régularisation liés à un PV validé (BT type_initial PV). */
export function isPvEcartRegularisationWorkflowDocument$(
  documentService: DocumentService,
  doc: Record<string, unknown>
): Observable<boolean> {
  const rootId = resolveTransferWorkflowRootId(doc);
  if (rootId == null || rootId <= 0) {
    return of(false);
  }
  return documentService.getDocumentsByOriginalId(rootId).pipe(
    map((res) => {
      const children = parseConvertedChildrenFromApiResponse(res);
      const bt =
        pickLatestChildByType(children, STOCK_TRANSFER_WORKFLOW_TYPE_IDS.BON_TRANSFERT) ??
        (isBonTransfertDocumentFromPvEcart(doc) ? doc : null);
      return isBonTransfertDocumentFromPvEcart(bt as Record<string, unknown>);
    }),
    catchError(() => of(false))
  );
}

export type PvEcartRegulLcStockLigne = {
  code_barre?: string | null;
  ref_interne?: string | null;
  ref_fabricant?: string | null;
  qnt: number;
  sens: 'MANQUANT' | 'EXCEDENT';
  libelle?: string | null;
  product_id?: number | null;
};

/** Lignes ERP approve → stock LC avec sens manquant / excédent. */
export function buildPvEcartRegulStockLignesFromErpLignes(
  lignes: Array<Record<string, unknown>>
): PvEcartRegulLcStockLigne[] {
  const items = mapErpLignesToPvRegularisationItems(lignes);
  return buildStockLignesFromPvDocumentItems(items).map((s) => ({
    code_barre: s.code_bar,
    ref_interne: s.ref_interne,
    ref_fabricant: s.ref_fabricant,
    qnt: s.qnt,
    sens: s.sens,
    libelle: null,
  }));
}
